/**
 * Strategy View
 * Betting strategies and tips
 *
 * This view uses a hybrid approach:
 * - Rule-based strategies: Generated from betting patterns
 * - AI-enhanced narratives: Detailed explanations and implementation steps (when available)
 */

import { AlertBox, LoadingSpinner } from '@components/ui';
import type { AlertVariant } from '@components/ui/AlertBox';
import { NarrativeDisplay } from '@components/ui/NarrativeDisplay';
import { type ChecklistItem, useChecklist } from '@hooks/useChecklist';
import type { Strategy } from '@hooks/useInsights';
import { useInsights } from '@hooks/useInsights';
import { ChevronDown, PlusCircle, RefreshCw, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface StrategyCardProps {
  strategy: Strategy;
}

// =============================================================================
// Main Component
// =============================================================================

export function StrategyView(): ReactElement {
  const { strategies, loading, refreshing, refreshInsights } = useInsights();
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Handle refresh with error display
  const handleRefresh = async () => {
    setRefreshError(null);
    const result = await refreshInsights();
    if (!result.success && result.error) {
      setRefreshError(result.error);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" message="Loading strategies..." />;
  }

  // If user has personalized strategies from DB, show those
  if (strategies && strategies.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Your Personalized Strategies</h2>
          <button
            type="button"
            data-testid="strategy-refresh-button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-theme-surface-hover hover:bg-theme-surface-hover rounded text-sm text-theme-text-secondary transition"
          >
            {refreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </>
            )}
          </button>
        </div>

        {/* Refresh Error Display */}
        {refreshError && (
          <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-3 flex items-center justify-between">
            <span className="text-orange-300 text-sm">{refreshError}</span>
            <button
              type="button"
              onClick={() => setRefreshError(null)}
              className="text-orange-400 hover:text-orange-300 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <p className="text-theme-text-secondary text-sm">
          These strategies are tailored to your betting history and patterns.
        </p>

        {strategies.map((strategy) => (
          <StrategyCard key={strategy.id} strategy={strategy} />
        ))}
      </div>
    );
  }

  // Fallback to static strategies if no personalized ones
  return <StaticStrategies />;
}

// =============================================================================
// Sub-components
// =============================================================================

function StrategyCard({ strategy }: StrategyCardProps): ReactElement {
  const [showNarrative, setShowNarrative] = useState(false);
  const tips = strategy.tips || [];
  // Cast variant with fallback to valid AlertVariant
  const variant = (strategy.variant as AlertVariant) || 'blue';
  const hasAINarrative = !!strategy.aiNarrative;

  return (
    <AlertBox variant={variant} title={strategy.title}>
      {strategy.description && (
        <p className="text-theme-text-secondary text-sm mb-3">{strategy.description}</p>
      )}
      <ul className="space-y-2 text-sm">
        {tips.map((tip, idx) => (
          <li key={idx}>{tip.startsWith('-') ? tip : `- ${tip}`}</li>
        ))}
      </ul>

      {/* AI Narrative (expandable) */}
      {hasAINarrative && (
        <div className="mt-3 pt-3 border-t border-theme-border/50">
          <button
            type="button"
            onClick={() => setShowNarrative(!showNarrative)}
            className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            {showNarrative ? 'Hide AI Details' : 'Show AI-Powered Details'}
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showNarrative ? 'rotate-180' : ''}`}
            />
          </button>

          {showNarrative && (
            <div className="mt-3 p-3 bg-theme-surface/50 rounded border-l-2 border-purple-500">
              <div className="flex items-center gap-1 text-xs text-purple-400 mb-2">
                <PlusCircle className="w-3 h-3" />
                AI Analysis
              </div>
              <NarrativeDisplay narrative={strategy.aiNarrative!} />
              <p className="text-gray-600 text-xs italic mt-2">
                AI-generated content for informational purposes only.
              </p>
            </div>
          )}
        </div>
      )}

      {strategy.basedOn != null && <BasedOnInfo basedOn={strategy.basedOn} />}
    </AlertBox>
  );
}

// Helper to safely render basedOn info from unknown type
function BasedOnInfo({ basedOn }: { basedOn: unknown }): ReactElement | null {
  if (!basedOn || typeof basedOn !== 'object') return null;

  const data = basedOn as { bets?: number; count?: number; winRate?: string };
  const betCount = data.bets || data.count;

  return (
    <p className="text-theme-text-tertiary text-xs mt-3">
      Based on your {betCount || 'betting'} data
      {data.winRate && ` (${data.winRate}% win rate)`}
    </p>
  );
}

function StaticStrategies(): ReactElement {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-white">Betting Strategies</h2>
      <p className="text-theme-text-secondary text-sm">
        Start tracking bets to get personalized strategies. Here are general tips:
      </p>

      <AlertBox variant="indigo" title="Basketball Player Props Strategy">
        <p className="text-theme-text-secondary text-sm mb-3">
          Key factors for successful prop betting:
        </p>
        <ul className="space-y-2 text-sm">
          <li>
            <strong>Check minutes projections first:</strong> A player's prop is meaningless without
            context. Use sites like FantasyLabs or Hashtag Basketball for projected minutes.
          </li>
          <li>
            <strong>Target pace mismatches:</strong> Fast-paced games (vs. Pacers, Hawks, Jazz)
            inflate counting stats. Slow games (vs. Knicks, Heat, Cavs defense) suppress them.
          </li>
          <li>
            <strong>Assists props are sticky:</strong> Unlike points, assists correlate with role.
            Playmakers like Mitchell, Garland, and White hit assist floors consistently.
          </li>
          <li>
            <strong>Rebounds favor matchups:</strong> Target bigs against poor rebounding teams.
            Check team rebounding rates on NBA.com/stats.
          </li>
          <li>
            <strong>Fade stars in blowouts:</strong> Starters sit in 4th quarter of blowouts. If a
            spread is 10+, consider unders.
          </li>
          <li>
            <strong>Back-to-backs matter:</strong> Stars often rest or see reduced minutes. Check
            the schedule before betting overs.
          </li>
        </ul>
      </AlertBox>

      <AlertBox variant="rose" title="Same Game Parlay (SGP) Strategy">
        <p className="text-theme-text-secondary text-sm mb-3">
          SGPs have high vig, but you can find edges:
        </p>
        <ul className="space-y-2 text-sm">
          <li>
            <strong>Correlate intelligently:</strong> If you bet a team to win big, pair with their
            star's overs AND opponent star's unders (less time on court).
          </li>
          <li>
            <strong>Avoid anti-correlation:</strong> Don't pair two players' points overs on the
            same team - if one goes off, the other often doesn't.
          </li>
          <li>
            <strong>Keep it to 2-3 legs:</strong> Every leg you add hands more edge to the house.
          </li>
          <li>
            <strong>Use SGPs for correlation, not volume:</strong> The only reason to SGP is to
            exploit correlation the book underprices.
          </li>
          <li>
            <strong>Check alt lines:</strong> Sometimes "Player X 15+ points" at -200 in an SGP is
            worse value than their O14.5 at -115 straight.
          </li>
        </ul>
      </AlertBox>

      <AlertBox variant="amber" title="NFL Props Strategy">
        <p className="text-theme-text-secondary text-sm mb-3">Tips for NFL player props:</p>
        <ul className="space-y-2 text-sm">
          <li>
            <strong>Target volume, not talent:</strong> Receptions and targets matter more than
            skill. WR1s with 8+ targets/game are consistent.
          </li>
          <li>
            <strong>ATTS is high variance:</strong> Touchdown props are essentially coin flips in
            the red zone. Avoid in parlays.
          </li>
          <li>
            <strong>Weather kills passing:</strong> Check forecasts. Wind 15+ mph and rain crush
            receiving props.
          </li>
          <li>
            <strong>Game script matters:</strong> Teams trailing pass more. If you expect a blowout,
            target the underdog's receivers.
          </li>
          <li>
            <strong>Red zone usage:</strong> For ATTS, check red zone target share on PlayerProfiler
            or FantasyPros.
          </li>
        </ul>
      </AlertBox>

      <DynamicChecklist />
    </div>
  );
}

const CATEGORY_LABELS: Record<ChecklistItem['category'], string> = {
  discipline: 'Discipline',
  bankroll: 'Bankroll',
  research: 'Research',
  timing: 'Timing',
};

const CATEGORY_COLORS: Record<ChecklistItem['category'], string> = {
  discipline: 'text-theme-primary',
  bankroll: 'text-theme-success',
  research: 'text-theme-warning',
  timing: 'text-purple-400',
};

function DynamicChecklist(): ReactElement {
  const { items, isPersonalized, loading, error } = useChecklist();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <AlertBox variant="teal" title="Pre-Bet Checklist">
        <div className="flex items-center gap-2 py-4">
          <div className="w-4 h-4 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-theme-text-secondary text-sm">Loading your checklist...</span>
        </div>
      </AlertBox>
    );
  }

  if (error || items.length === 0) {
    return (
      <AlertBox variant="teal" title="Pre-Bet Checklist">
        <p className="text-theme-text-tertiary text-sm py-2">
          Could not load checklist. Track at least 5 bets to get personalized items.
        </p>
      </AlertBox>
    );
  }

  return (
    <AlertBox variant="teal" title="Pre-Bet Checklist">
      <p className="text-theme-text-secondary text-sm mb-3">
        {isPersonalized
          ? 'Personalized based on your betting patterns:'
          : 'Run through this before placing any bet:'}
      </p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            <button
              type="button"
              onClick={() => toggleItem(item.id)}
              className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                checked.has(item.id)
                  ? 'bg-theme-success border-theme-success'
                  : 'border-theme-border hover:border-theme-text-secondary'
              }`}
            >
              {checked.has(item.id) && <span className="text-white text-[10px] font-bold">✓</span>}
            </button>
            <div className="flex-1">
              <span
                className={`text-sm ${checked.has(item.id) ? 'text-theme-text-tertiary' : 'text-theme-text-secondary'}`}
              >
                <span className={`text-xs font-medium ${CATEGORY_COLORS[item.category]} mr-1.5`}>
                  [{CATEGORY_LABELS[item.category]}]
                </span>
                {item.text}
              </span>
              {item.reason && (
                <p className="text-xs text-theme-text-tertiary mt-0.5 ml-0.5">{item.reason}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {checked.size === items.length && items.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border/50">
          <p className="text-theme-success text-sm font-medium">
            All checks passed — you're good to place this bet.
          </p>
        </div>
      )}
    </AlertBox>
  );
}
